import { useAuth } from "@/app/providers";
import type { RouteConfig } from "@/app/router/routes";
import { routes } from "@/app/router/routes";
import { useUiStore } from "@/app/store/useUiStore";
import { Button } from "@/shared/components/ui/button";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/libs/utils";
import { ChevronDown, ChevronLeft, ChevronRight, Settings } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

function isPathActive(currentPathname: string, targetPath: string) {
  if (targetPath === "/") return currentPathname === "/";
  return currentPathname === targetPath || currentPathname.startsWith(`${targetPath}/`);
}

export function Sidebar() {
  const location = useLocation();
  const { isSidebarCollapsed, toggleSidebarCollapse } = useUiStore();
  const { grant } = useAuth();
  const [isManageOpen, setIsManageOpen] = useState<boolean>(false);

  const visibleRoutes = routes.filter((r) => !r.adminOnly || grant === "admin");
  const oncallRoutes = visibleRoutes.filter((r) => r.group === "oncall");
  const manageRoutes = visibleRoutes.filter((r) => r.group === "manage");

  const renderRouteItem = (route: RouteConfig, indent = false) => {
    const Icon = route.icon;
    const active = isPathActive(location.pathname, route.path);

    if (isSidebarCollapsed) {
      return (
        <Tooltip key={route.id}>
          <TooltipTrigger asChild>
            <Link to={route.path}>
              <Button
                variant={active ? "secondary" : "ghost"}
                className={cn(
                  "w-full justify-center h-11 hover:bg-accent",
                  active && "bg-sidebar-accent text-sidebar-accent-foreground"
                )}
                size="icon"
              >
                <Icon className="h-5 w-5" />
              </Button>
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">
            <span className="text-sm font-medium">{route.name}</span>
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Link to={route.path} key={route.id}>
        <Button
          variant={active ? "secondary" : "ghost"}
          className={cn(
            "w-full justify-start gap-3 h-11 px-4 hover:bg-accent",
            indent && "pl-8",
            active && "bg-sidebar-accent text-sidebar-accent-foreground"
          )}
        >
          <Icon className="h-5 w-5 shrink-0" />
          <span className="truncate text-sm">{route.name}</span>
        </Button>
      </Link>
    );
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen border-r bg-sidebar transition-all duration-300",
        isSidebarCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-center border-b px-4">
        {!isSidebarCollapsed ? (
          <Link to="/overview" className="flex items-center gap-2">
            <img
              src="/oncall-icon.png"
              alt="Oncall Management"
              className="h-8 w-8 shrink-0 rounded-lg object-contain"
            />
            <span className="text-lg font-bold text-sidebar-foreground">Oncall Management</span>
          </Link>
        ) : (
          <Link to="/overview">
            <img
              src="/oncall-icon.png"
              alt="Oncall Management"
              className="h-8 w-8 rounded-lg object-contain"
            />
          </Link>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="h-[calc(100vh-8rem)]">
        <nav className="space-y-2 p-3">
          <TooltipProvider delayDuration={0}>
            {oncallRoutes.map((r) => renderRouteItem(r))}

            <div className="my-2 mx-1 border-t border-border/40" />

            {isSidebarCollapsed ? (
              manageRoutes.map((r) => renderRouteItem(r))
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-11 px-4 hover:bg-accent"
                  onClick={() => setIsManageOpen((open) => !open)}
                  aria-expanded={isManageOpen}
                >
                  <Settings className="h-5 w-5 shrink-0" />
                  <span className="truncate text-sm flex-1 text-left">관리</span>
                  {isManageOpen ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                </Button>
                {isManageOpen && manageRoutes.map((r) => renderRouteItem(r, true))}
              </>
            )}
          </TooltipProvider>
        </nav>
      </ScrollArea>

      {/* Collapse Toggle */}
      <div className="absolute bottom-0 left-0 right-0 border-t p-2">
        <Button
          variant="ghost"
          size={isSidebarCollapsed ? "icon" : "sm"}
          className={cn("w-full", !isSidebarCollapsed && "justify-start gap-2")}
          onClick={toggleSidebarCollapse}
        >
          {isSidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4" />
              <span>접기</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}
